package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Holds the set of **saved (bookmarked) news article ids** as observable state, persisted via
 * [AppPreferences] so a reading list survives restarts. Same shape as [FavoritesController] on
 * purpose — the Saved screen and the news surfaces observe it to render and toggle the state.
 *
 * Signed-in accounts merge with and mirror to `users/{uid}.savedNewsIds` via `UserPreferencesSync`
 * (`:shared`); guests stay device-local until they upgrade. That is deliberately unlike
 * [SavedItemsController] (marketplace), which stays device-local because peer listings are
 * transient — news is curated and long-lived, so it should follow the account.
 */
class SavedNewsController(private val preferences: AppPreferences) {
    private val _saved = MutableStateFlow(preferences.savedNewsIds)
    val saved: StateFlow<Set<String>> = _saved.asStateFlow()

    fun isSaved(articleId: String): Boolean = articleId in _saved.value

    /** Add the id if absent, remove it if present; persists and emits the new set. */
    fun toggle(articleId: String) {
        if (articleId.isBlank()) return
        val next = _saved.value.toMutableSet().apply { if (!add(articleId)) remove(articleId) }
        preferences.savedNewsIds = next
        _saved.value = next
    }

    /** Replaces the whole set (sign-in merge with the account's saved ids); persists and emits. */
    fun replaceAll(ids: Set<String>) {
        preferences.savedNewsIds = ids
        _saved.value = ids
    }
}
